// Presentation mapper for the /lawn-care city-directory hub. Turns the
// verbose, sourced CityData records into the clean chips + one-line hook the
// redesigned hub card needs. The canonical data and its generator are left
// untouched -- everything here is either deterministically derived from an
// existing field or a small hand-authored override. New cities work with no
// extra wiring (the hook falls back to citySpecificNote).
//
// Design source: Claude Design "Paywall" project, "Local Lawn Care Guides.html".
// Consumed by app/lawn-care/page.tsx -> app/lawn-care/page-content.tsx.

import type { CityData } from './types'

export type CitySeason = 'cool' | 'warm' | 'transition'

export interface CityCardView {
  slug: string
  city: string
  state: string
  region: string
  season: CitySeason
  seasonLabel: string
  zoneShort: string
  climateLabel: string
  grassShort: string
  hook: string
  href: string
}

export const SEASON_LABEL: Record<CitySeason, string> = {
  cool: 'Cool-season',
  warm: 'Warm-season',
  transition: 'Transition zone',
}

// Hand-authored card hooks, lifted verbatim from the approved Claude Design
// prototype. Keyed by slug; any city without an entry falls back to the first
// sentence of its (sourced) citySpecificNote so the grid never renders blank.
const HOOKS: Record<string, string> = {
  'atlanta-ga':
    'Acidic red clay and humid heat. Lime the soil up, run Bermuda or Zoysia in full sun, and save tall fescue for the shade.',
  'charlotte-nc':
    'The textbook transition zone. Tall fescue stays green nearly year round, with bermuda and zoysia as the warm-season alternatives.',
  'chicago-il':
    'Heavy prairie clay and a short season. Fall core aeration does more for the lawn than almost anything you do in spring.',
  'denver-co':
    'Mile-high sun and alkaline clay. Irrigation discipline and fall aeration matter more here than almost anywhere.',
  'seattle-wa':
    'Wet, mild, and mossy. Moss control, drainage, and routine liming are the whole game here, not crabgrass.',
  'minneapolis-mn':
    'The coldest, shortest-season lawn in the cohort: single-digit January lows, snow mold under deep snow, and road-salt burn along the curb. Fall is your money season here, not spring.',
  'dallas-tx':
    'Blackland Prairie black gumbo that cracks open dry and seals shut wet, on alkaline clay that yellows lawns by locking up iron. Bermuda rules the sun, and your pre-emergent goes down in February, not April.',
  'new-york-ny':
    'Two lawns, one metro: fast-draining Long Island sand that needs frequent water, and compacting Westchester clay till that needs aeration. Both cool-season, zone 7b, with a mid-April crabgrass window keyed to forsythia.',
  'houston-tx':
    'Fifty inches of rain a year, heavy coastal gumbo clay, and 300 frost-free days. Here disease and chinch bugs, not drought, wreck a lawn, and St. Augustine is the default for a reason.',
  'memphis-tn':
    'Acidic loess silt loam, 54 inches of rain, and a true transition-zone summer. In Memphis you commit to fescue or bermuda, then lime the soil to fix the pH almost nobody tests for.',
}

// Köppen-code -> friendly label overrides for the cases where the parenthetical
// gloss reads awkwardly (e.g. Csb = "warm-summer Mediterranean / marine west
// coast"). Anything not listed falls through to the parsed parenthetical.
const KOPPEN_LABEL: Record<string, string> = {
  Csb: 'Marine west coast',
}

/** Normalize the descriptive grassSeasonType string to a season bucket.
 *  e.g. "warm-season (transition-zone city; ...)" -> "warm". */
export function seasonOf(grassSeasonType: string): CitySeason {
  const s = grassSeasonType.trim().toLowerCase()
  if (s.startsWith('warm')) return 'warm'
  if (s.startsWith('transition')) return 'transition'
  return 'cool'
}

/** Pull the leading hardiness-zone token from the verbose usdaZone string.
 *  e.g. "6a (most of the city), with 6b..." -> "6a"; "5b-6a (2023 map)" -> "5b-6a". */
export function shortZone(usdaZone: string): string {
  const m = usdaZone.trim().match(/^\d{1,2}[ab]?(?:\s*[-–]\s*\d{1,2}[ab]?)?/i)
  return m ? m[0].replace(/\s*[-–]\s*/, '-') : usdaZone.trim()
}

/** Friendly climate label from the Köppen string.
 *  e.g. "Dfa (humid continental, hot summer)" -> "Humid continental". */
export function climateLabel(koppen: string): string {
  const code = koppen.trim().split(/\s+/)[0]
  if (KOPPEN_LABEL[code]) return KOPPEN_LABEL[code]
  const paren = koppen.match(/\(([^)]+)\)/)?.[1] ?? koppen
  const first = paren.split(/[,/]/)[0].trim()
  return first.charAt(0).toUpperCase() + first.slice(1)
}

/** Primary grass name without the cultivar parenthetical.
 *  e.g. "Bermudagrass (TifTuf, Tifway 419)" -> "Bermudagrass". */
export function shortGrass(grass: string): string {
  return grass.split('(')[0].trim()
}

/** First sentence of a longer note, used as a hook fallback. */
function firstSentence(text: string): string {
  const trimmed = text.trim()
  const m = trimmed.match(/^[^.]*\./)
  return (m ? m[0] : trimmed).trim()
}

export function toCityCardView(c: CityData): CityCardView {
  const season = seasonOf(c.grassSeasonType)
  return {
    slug: c.slug,
    city: c.city,
    state: c.state,
    region: c.region,
    season,
    seasonLabel: SEASON_LABEL[season],
    zoneShort: shortZone(c.usdaZone),
    climateLabel: climateLabel(c.koppen),
    grassShort: shortGrass(c.bestGrass[0]?.grass ?? ''),
    hook: HOOKS[c.slug] ?? firstSentence(c.citySpecificNote),
    href: `/lawn-care/${c.slug}`,
  }
}
